import type {TextSelection} from "prosemirror-state";
import React from "react";
import intl from "react-intl-universal";


import {H1Icon} from "../../../../components/icons/editor/H1Icon";
import {H2Icon} from "../../../../components/icons/editor/H2Icon";
import {H3Icon} from "../../../../components/icons/editor/H3Icon";
import {BulletListIcon} from "../../../../components/icons/editor/BulletListIcon";
import {NumberListIcon} from "../../../../components/icons/editor/NumberListIcon";
import {CheckListIcon} from "../../../../components/icons/editor/CheckListIcon";
import {UploadFileIcon} from "../../../../components/icons/editor/UploadFileIcon";
import {UploadImageIcon} from "../../../../components/icons/editor/UploadImageIcon";
import {TableIcon} from "../../../../components/icons/editor/TableIcon";
import {QuoteIcon} from "../../../../components/icons/editor/QuoteIcon";
import {CalloutIcon} from "../../../../components/icons/editor/CalloutIcon";
import {MentionIcon} from "../../../../components/icons/editor/MentionIcon";
import {HorizontalLineIcon} from "../../../../components/icons/editor/HorizontalLineIcon";

import {MuiIconExternalProps} from "../../../../components/icons/MuiIcon";
import {ContextMenuInEditor} from '../../../../components/ContextMenuInEditor';

import {insertHeading} from '../../../../../Editor/module/commands/triggers/insertHeading';
import {insertHorizontalLine} from '../../../../../Editor/module/commands/triggers/insertHorizontalLine';
import {uploadImageCommand} from '../../../../../Editor/module/commands/triggers/uploadImageCommand';
import {uploadFileCommand} from '../../../../../Editor/module/commands/triggers/uploadFileCommand';
import {insertList} from '../../../../../Editor/module/commands/triggers/insertList';
import {insertTable} from '../../../../../Editor/module/commands/triggers/insertTable';
import {insertBlockquote} from "../../../../../Editor/module/commands/triggers/insertBlockquote";
import {insertCallout} from "../../../../../Editor/module/commands/triggers/insertCallout";
import {insertMentionTrigger} from "../../../../../Editor/module/commands/triggers/insertMentionTrigger";


import {ExtendedCommand} from "../utils/GroupedActions";


import { editorViewRef } from '../../../../elements/EditorReact';
import { FEATURE_FLAGS, isFeatureFlagEnabled } from '../../../../../stores/featureFlags';


interface TriggerAction {
    id: string;
    Icon: React.ComponentType<MuiIconExternalProps>;
    getTitle: () => string;
    action: ExtendedCommand;
    isInline?: boolean;
    preserveContextMenu?: boolean;
    isVisible?: () => boolean;
}

const headingActionsGroup: TriggerAction[] = [
    {
        id: 'H1',
        Icon: H1Icon,
        getTitle: () => intl.get('trigger-actions.large_heading').d('Large heading'),
        action: insertHeading(1),
        isInline: false,
    },
    {
        id: 'H2',
        Icon: H2Icon,
        getTitle: () => intl.get('trigger-actions.medium_heading').d('Medium heading'),
        action: insertHeading(2),
        isInline: false,
    },
    {
        id: 'H3',
        Icon: H3Icon,
        getTitle: () => intl.get('trigger-actions.small_heading').d('Small heading'),
        action: insertHeading(3),
        isInline: false,
    },
];

const listActionsGroup: TriggerAction[] =[
    {
        id: 'BulletList',
        Icon: BulletListIcon,
        getTitle: () => intl.get('trigger-actions.bullet_list').d('Bulleted list'),
        action: insertList('bullet'),
        isInline: false,
    },
    {
        id: 'NumberList',
        Icon: NumberListIcon,
        getTitle: () => intl.get('trigger-actions.number_list').d('Numbered list'),
        action: insertList('ordered'),
        isInline: false,
    },
    {
        id: 'CheckList',
        Icon: CheckListIcon,
        getTitle: () => intl.get('trigger-actions.task_list').d('Task list'),
        action: insertList('checklist'),
        isInline: false,
        isVisible: () => isFeatureFlagEnabled({} as any, FEATURE_FLAGS.CHECKLIST),
    },
];

const thirdActionsGroup: TriggerAction[] = [
    {
        id: 'UploadFile',
        Icon: UploadFileIcon,
        getTitle: () => intl.get('trigger-actions.upload_file').d('Upload file'),
        action: uploadFileCommand,
        isInline: true,
    },
    {
        id: 'UploadImage',
        Icon: UploadImageIcon,
        getTitle: () => intl.get('trigger-actions.upload_image').d('Upload image'),
        action: uploadImageCommand,
        isInline: true,
    },
    {
        id: 'Table',
        Icon: TableIcon,
        getTitle: () => intl.get('trigger-actions.table').d('Table'),
        action: insertTable,
        isInline: false,
    },
];

const otherActionsGroup: TriggerAction[] = [
/*    {
        id: 'AppsAndIntegrations',
        Icon: AppsAndIntegrationsIcon,
        getTitle: () => intl.get('trigger-actions.apps_and_integrations').d('Apps & Integration'),
        action: insertHeading(3),
        isInline: false,
    },
    {
        id: 'CodeBlock',
        Icon: CodeBlockIcon,
        getTitle: () => intl.get('trigger-actions.code_block').d('Code block'),
        action: insertHeading(3),
        isInline: false,
    },*/
    {
        id: 'Quote',
        Icon: QuoteIcon,
        getTitle: () => intl.get('trigger-actions.quote').d('Quote'),
        action: insertBlockquote,
        isInline: false,
    },
    {
        id: 'Callout',
        Icon: CalloutIcon,
        getTitle: () => intl.get('trigger-actions.callout').d('Callout'),
        action: insertCallout,
        isInline: false,
    },
    {
        id: 'HorizontalLine',
        Icon: HorizontalLineIcon,
        getTitle: () => intl.get('trigger-actions.horizontal_line').d('Horizontal line'),
        action: insertHorizontalLine,
        isInline: false,
    },
    {
        id: 'Mention',
        Icon: MentionIcon,
        getTitle: () => intl.get('trigger-actions.mention').d('Mention'),
        action: insertMentionTrigger,
        preserveContextMenu: true,
        isInline: true,
    },
];

const itemGroups: TriggerAction[][] = [
    headingActionsGroup,
    listActionsGroup,
    thirdActionsGroup,
    otherActionsGroup,
]

export interface TriggerActionsProps {
    textInput: string;
    handleClose: () => void;
    isDark: boolean;
    isTriggerInline: boolean;
    nodePos: number;
}


export const TriggerActions = ({textInput, handleClose, isDark, isTriggerInline, nodePos}: TriggerActionsProps) => {
    const view = editorViewRef.current!;
    const filteredGroups = [];
    for (const group of itemGroups) {
        const filteredItems = [];
        for (const ctxMenuItem of group) {
            if (ctxMenuItem.isVisible && !ctxMenuItem.isVisible()) {
                continue;
            }
            if (!isTriggerInline || ctxMenuItem.isInline) {
                filteredItems.push(ctxMenuItem);
            }
        }
        if (filteredItems.length) {
            filteredGroups.push(filteredItems);
        }
    }

    return (
        <ContextMenuInEditor
            headerText={intl.get('trigger-actions.type_to_filter').d('Type to filter')}
            isDark={isDark}
            itemGroups={filteredGroups.map(itemGroup => itemGroup.map(ctxMenuItemLine => ({
                ...ctxMenuItemLine,
                text: ctxMenuItemLine.getTitle(),
                onClick: () => {
                    return ctxMenuItemLine.action(view.state, view.dispatch, view, {
                        nodePos: nodePos,
                        textInput,
                    });
                }
            })))}
            onEmptyEnter={() => {
                const selection = (view.state.selection as TextSelection);
                view.dispatch(view.state.tr.insertText('\n', selection.$anchor.pos));
                return;
            }}
            handleClose={handleClose}
            isInline={true}
            filterQuery={textInput}
        />
    )
}
